import { useState, useMemo } from "react";
import { formatNumber } from "@/lib/units";
import ResultsPanel, { CalcPageHeader, Field } from "@/components/ResultsPanel";

const MODES = [
  { key: "increase", label: "% Increase" },
  { key: "decrease", label: "% Decrease" },
  { key: "margin",   label: "Margin" },
  { key: "markup",   label: "Markup" },
  { key: "gstAdd",   label: "GST Add" },
  { key: "gstRem",   label: "GST Remove" },
];

export default function PercentageCalculator() {
  const [mode, setMode] = useState("increase");
  const [a, setA] = useState("1000");
  const [b, setB] = useState("18");

  const { primary, rows } = useMemo(() => {
    const A = Number(a) || 0;
    const B = Number(b) || 0;
    switch (mode) {
      case "increase": {
        const inc = A * (1 + B/100);
        return { primary: inc, rows: [["Original", A],["Percentage",`${B}%`],["Increase amount",A*B/100],["Final value",inc]] };
      }
      case "decrease": {
        const dec = A * (1 - B/100);
        return { primary: dec, rows: [["Original", A],["Percentage",`${B}%`],["Decrease amount",A*B/100],["Final value",dec]] };
      }
      case "margin": {
        // A = cost, B = selling price; margin % = (SP-CP)/SP
        const margin = B > 0 ? ((B - A) / B) * 100 : 0;
        return { primary: margin, rows: [["Cost price", A],["Selling price", B],["Profit", B-A],["Margin %", `${formatNumber(margin,2)}%`]] };
      }
      case "markup": {
        const markup = A > 0 ? ((B - A) / A) * 100 : 0;
        return { primary: markup, rows: [["Cost price", A],["Selling price", B],["Profit", B-A],["Markup %", `${formatNumber(markup,2)}%`]] };
      }
      case "gstAdd": {
        const tax = A * B/100;
        return { primary: A+tax, rows: [["Base amount",A],["GST %",`${B}%`],["Tax",tax],["Inclusive total",A+tax]] };
      }
      case "gstRem": {
        const base = A / (1 + B/100);
        return { primary: base, rows: [["Inclusive amount",A],["GST %",`${B}%`],["Tax",A-base],["Base amount",base]] };
      }
      default: return { primary: 0, rows: [] };
    }
  }, [mode, a, b]);

  const labels = {
    increase: { a: "Value", b: "Increase %" },
    decrease: { a: "Value", b: "Decrease %" },
    margin:   { a: "Cost price", b: "Selling price" },
    markup:   { a: "Cost price", b: "Selling price" },
    gstAdd:   { a: "Base amount", b: "GST %" },
    gstRem:   { a: "Inclusive amount", b: "GST %" },
  };

  return (
    <div>
      <CalcPageHeader title="Percentage Calculator" description="Percentage increase/decrease, margin, markup and GST addition/removal." slug="percentage" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-7">
          <div className="rounded-lg border border-border bg-card p-5 sm:p-6 space-y-4">
            <Field label="Mode">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="pct-mode-group">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    data-testid={`pct-mode-${m.key}`}
                    onClick={()=>setMode(m.key)}
                    className={`h-10 px-3 rounded-md text-sm font-medium border transition ${mode===m.key ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:bg-accent"}`}
                  >{m.label}</button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={labels[mode].a}><input data-testid="pct-a" type="number" value={a} onChange={(e)=>setA(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
              <Field label={labels[mode].b}><input data-testid="pct-b" type="number" value={b} onChange={(e)=>setB(e.target.value)} className="w-full h-11 px-3 rounded-md border border-border bg-background font-mono outline-none focus:ring-2 focus:ring-primary/40" /></Field>
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ResultsPanel
            title={`Percentage · ${MODES.find(m=>m.key===mode).label}`}
            slug="percentage"
            highlight={{ label: "Result", value: typeof primary === "number" ? formatNumber(primary,2) : primary }}
            rows={rows.map(([k,v])=>[k, typeof v === "number" ? formatNumber(v,2) : v])}
            onReset={()=>{setA("");setB("");}}
          />
        </div>
      </div>
    </div>
  );
}
